import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import {
  COL_TEMPLATES,
  DEFAULT_TEMPLATE_ID,
  type AutoAssignRole,
  type ChecklistTemplate,
  type Section,
  type TemplateItem,
} from "@closing/shared";
import { auth, db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { useDirectory } from "../lib/directory";

const EMPTY_TEMPLATE: ChecklistTemplate = {
  name: "Closing Checklist",
  sections: [],
  items: [],
  version: 0,
  updatedAt: null,
  updatedBy: "",
};

/**
 * Admin-only editor for closing-templates/default. Edits are local until
 * "Save" — each save bumps `version` and snapshots the previous state into
 * the versions/ subcollection. Live checklists are never touched (they copy
 * the template at instantiation).
 */
export default function TemplateEditor() {
  const { isAdmin } = useAuth();
  const [template, setTemplate] = useState<ChecklistTemplate | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDoc(doc(db, COL_TEMPLATES, DEFAULT_TEMPLATE_ID))
      .then((snap) => {
        setTemplate(snap.exists() ? (snap.data() as ChecklistTemplate) : EMPTY_TEMPLATE);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  function mutate(fn: (t: ChecklistTemplate) => ChecklistTemplate) {
    setTemplate((t) => (t ? fn(t) : t));
    setDirty(true);
  }

  async function save() {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      const version = (template.version ?? 0) + 1;
      const next = {
        ...template,
        version,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email?.toLowerCase() ?? "",
      };
      const ref = doc(db, COL_TEMPLATES, DEFAULT_TEMPLATE_ID);
      await setDoc(ref, next);
      // Snapshot this version for history.
      await setDoc(doc(ref, "versions", String(version)), next);
      setTemplate(next);
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const sections = useMemo(
    () => (template ? [...template.sections].sort((a, b) => a.sortOrder - b.sortOrder) : []),
    [template],
  );

  if (isAdmin === false) {
    return <p className="eyebrow py-20 text-center">Template editing is admin-only.</p>;
  }
  if (!template) {
    return <p className="eyebrow py-20 text-center">{error ?? "Loading…"}</p>;
  }

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-signature">Template</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{template.name}</h1>
          <p className="mt-1 font-mono text-[11px] text-muted">
            v{template.version} · {template.items.length} items · changes apply to newly created
            checklists only
          </p>
        </div>
        <div className="flex items-center gap-3">
          {error && <span className="font-mono text-[11px] text-signature">{error}</span>}
          <button onClick={() => void save()} disabled={!dirty || saving} className="btn-primary">
            {saving ? "Saving…" : dirty ? `Save as v${template.version + 1}` : "Saved"}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {sections.map((section, idx) => (
          <TemplateSection
            key={section.id}
            section={section}
            isFirst={idx === 0}
            isLast={idx === sections.length - 1}
            items={template.items
              .filter((i) => i.sectionId === section.id)
              .sort((a, b) => a.sortOrder - b.sortOrder)}
            template={template}
            mutate={mutate}
          />
        ))}
        <button
          onClick={() =>
            mutate((t) => ({
              ...t,
              sections: [
                ...t.sections,
                {
                  id: nanoid(8),
                  name: "New Section",
                  sortOrder: Math.max(0, ...t.sections.map((s) => s.sortOrder)) + 1,
                },
              ],
            }))
          }
          className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted hover:text-ink"
        >
          + add section
        </button>
      </div>
    </div>
  );
}

/**
 * Auto-assign rule for one template item: a deal role (research/legal),
 * a specific teammate, none (blank), or never (skip auto-assign entirely).
 */
function AssignRulePicker({
  item,
  mutate,
}: {
  item: TemplateItem;
  mutate: (fn: (t: ChecklistTemplate) => ChecklistTemplate) => void;
}) {
  const directory = useDirectory();
  const value = item.excludeFromAutoAssign
    ? "exclude"
    : item.autoAssignEmail
      ? `email:${item.autoAssignEmail}`
      : (item.autoAssignRole ?? "none");

  return (
    <select
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        mutate((t) => ({
          ...t,
          items: t.items.map((i) =>
            i.id === item.id
              ? {
                  ...i,
                  excludeFromAutoAssign: v === "exclude",
                  autoAssignEmail: v.startsWith("email:") ? v.slice(6) : null,
                  autoAssignRole:
                    v === "research" || v === "legal" ? (v as AutoAssignRole) : null,
                }
              : i,
          ),
        }));
      }}
      className="rounded border border-line bg-white px-1 py-0.5 font-mono text-[10px] text-ink-soft"
    >
      <option value="research">research contact</option>
      <option value="legal">legal contact</option>
      <option value="none">none</option>
      <option value="exclude">never</option>
      <optgroup label="specific person">
        {directory.map((d) => (
          <option key={d.email} value={`email:${d.email}`}>
            {d.displayName}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

/**
 * Gate picker: the item that must complete before this one can be checked
 * off. Stored as an array (dependsOn) but the team's gates are all single
 * predecessors, so the UI keeps it to one.
 *
 * Cycle guard: a candidate is excluded when it TRANSITIVELY depends on this
 * item — picking it would close a cycle (e.g. Write → Send → Create →
 * Write) whose members could then never be completed, since every gate in
 * the loop stays incomplete forever.
 */
function GatePicker({
  item,
  template,
  mutate,
}: {
  item: TemplateItem;
  template: ChecklistTemplate;
  mutate: (fn: (t: ChecklistTemplate) => ChecklistTemplate) => void;
}) {
  const dependsOnById = new Map(template.items.map((i) => [i.id, i.dependsOn ?? []]));
  // True when `fromId` reaches `targetId` by following dependsOn edges.
  // The visited set also keeps this safe on (hypothetical) pre-existing cycles.
  const reaches = (fromId: string, targetId: string, seen = new Set<string>()): boolean => {
    if (fromId === targetId) return true;
    if (seen.has(fromId)) return false;
    seen.add(fromId);
    return (dependsOnById.get(fromId) ?? []).some((d) => reaches(d, targetId, seen));
  };
  const options = template.items.filter((i) => i.id !== item.id && !reaches(i.id, item.id));
  return (
    <select
      value={item.dependsOn?.[0] ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        mutate((t) => ({
          ...t,
          items: t.items.map((i) =>
            i.id === item.id ? { ...i, dependsOn: v ? [v] : [] } : i,
          ),
        }));
      }}
      className="w-full rounded border border-line bg-white px-1 py-0.5 font-mono text-[10px] text-ink-soft"
    >
      <option value="">—</option>
      {options.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name.length > 34 ? i.name.slice(0, 33) + "…" : i.name}
        </option>
      ))}
    </select>
  );
}

function TemplateSection({
  section,
  items,
  template,
  isFirst,
  isLast,
  mutate,
}: {
  section: Section;
  items: TemplateItem[];
  template: ChecklistTemplate;
  isFirst: boolean;
  isLast: boolean;
  mutate: (fn: (t: ChecklistTemplate) => ChecklistTemplate) => void;
}) {
  // Swap sortOrder with the neighbor in the given direction.
  function move(direction: "up" | "down") {
    mutate((t) => {
      const ordered = [...t.sections].sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = ordered.findIndex((s) => s.id === section.id);
      const neighbor = ordered[direction === "up" ? idx - 1 : idx + 1];
      if (idx < 0 || !neighbor) return t;
      const self = ordered[idx]!;
      return {
        ...t,
        sections: t.sections.map((s) =>
          s.id === self.id
            ? { ...s, sortOrder: neighbor.sortOrder }
            : s.id === neighbor.id
              ? { ...s, sortOrder: self.sortOrder }
              : s,
        ),
      };
    });
  }
  return (
    <section className="overflow-hidden rounded-[10px] border border-line bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-line-strong bg-paper px-4 py-2">
        <input
          value={section.name}
          onChange={(e) =>
            mutate((t) => ({
              ...t,
              sections: t.sections.map((s) =>
                s.id === section.id ? { ...s, name: e.target.value } : s,
              ),
            }))
          }
          className="min-w-0 flex-1 bg-transparent font-sans text-[11px] font-bold uppercase tracking-[0.08em] text-ink-soft outline-none"
        />
        <button
          onClick={() => move("up")}
          disabled={isFirst}
          className="font-mono text-[11px] text-muted hover:text-ink disabled:opacity-20"
          title="Move section up"
        >
          ↑
        </button>
        <button
          onClick={() => move("down")}
          disabled={isLast}
          className="font-mono text-[11px] text-muted hover:text-ink disabled:opacity-20"
          title="Move section down"
        >
          ↓
        </button>
        <button
          onClick={() => {
            const msg =
              items.length === 0
                ? `Delete the empty section "${section.name}" from the template?`
                : `Delete the section "${section.name}" AND its ${items.length} item(s) from the template?`;
            if (!window.confirm(msg)) return;
            mutate((t) => {
              const removedIds = new Set(
                t.items.filter((i) => i.sectionId === section.id).map((i) => i.id),
              );
              return {
                ...t,
                sections: t.sections.filter((s) => s.id !== section.id),
                items: t.items
                  .filter((i) => !removedIds.has(i.id))
                  // Surviving items must not gate on removed ones.
                  .map((i) =>
                    i.dependsOn?.some((d) => removedIds.has(d))
                      ? { ...i, dependsOn: i.dependsOn.filter((d) => !removedIds.has(d)) }
                      : i,
                  ),
              };
            });
          }}
          className="font-mono text-[11px] text-muted hover:text-flag"
          title="Delete section"
        >
          ×
        </button>
      </header>

      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-line font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
            <th className="px-4 py-1.5 text-left font-medium">Item</th>
            <th className="w-28 px-2 py-1.5 text-left font-medium">Auto-assign</th>
            <th className="w-32 px-2 py-1.5 text-left font-medium">Gated by</th>
            <th className="w-10 px-2 py-1.5 text-left font-medium" title="Show a Yes/No answer chip on this item">
              Y/N
            </th>
            <th className="w-8 px-2 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b border-line last:border-b-0">
              <td className="px-4 py-1.5">
                <input
                  value={item.name}
                  onChange={(e) =>
                    mutate((t) => ({
                      ...t,
                      items: t.items.map((i) =>
                        i.id === item.id ? { ...i, name: e.target.value } : i,
                      ),
                    }))
                  }
                  className="w-full bg-transparent outline-none"
                />
              </td>
              <td className="px-2 py-1.5">
                <AssignRulePicker item={item} mutate={mutate} />
              </td>
              <td className="px-2 py-1.5">
                <GatePicker item={item} template={template} mutate={mutate} />
              </td>
              <td className="px-2 py-1.5">
                <input
                  type="checkbox"
                  checked={!!item.hasYesNo}
                  onChange={(e) =>
                    mutate((t) => ({
                      ...t,
                      items: t.items.map((i) =>
                        i.id === item.id ? { ...i, hasYesNo: e.target.checked } : i,
                      ),
                    }))
                  }
                  className="accent-[#bb2027]"
                  title="Show a Yes/No answer chip on this item"
                />
              </td>
              <td className="px-2 py-1.5 text-right">
                <button
                  onClick={() => {
                    // One stray click shouldn't drop a template item (even
                    // though Save is still required to persist it).
                    if (!window.confirm(`Remove "${item.name}" from the template?`)) return;
                    mutate((t) => ({
                      ...t,
                      items: t.items
                        .filter((i) => i.id !== item.id)
                        // Drop gates pointing at the removed item.
                        .map((i) =>
                          i.dependsOn?.includes(item.id)
                            ? { ...i, dependsOn: i.dependsOn.filter((d) => d !== item.id) }
                            : i,
                        ),
                    }));
                  }}
                  className="font-mono text-[11px] text-muted hover:text-flag"
                  title="Remove item"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} className="px-4 py-1.5">
              <input
                placeholder="+ add item — Enter to commit"
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  const name = (e.target as HTMLInputElement).value.trim();
                  if (!name) return;
                  (e.target as HTMLInputElement).value = "";
                  mutate((t) => ({
                    ...t,
                    items: [
                      ...t.items,
                      {
                        id: nanoid(10),
                        name,
                        description: null,
                        sectionId: section.id,
                        sortOrder:
                          Math.max(
                            0,
                            ...t.items
                              .filter((i) => i.sectionId === section.id)
                              .map((i) => i.sortOrder),
                          ) + 1,
                        autoAssignRole: "research",
                        autoAssignEmail: null,
                        excludeFromAutoAssign: false,
                        featureTags: [],
                      },
                    ],
                  }));
                }}
                className="w-full bg-transparent font-mono text-[11px] text-muted outline-none placeholder:text-faint"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
